// src/components/common/ImageUploadModal.js

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FiX, FiUpload, FiZoomIn, FiRefreshCw, FiImage, FiCheck } from 'react-icons/fi';
import { useToast } from '../shared/contexts/ToastContext';

// --- Constants & Helpers ---
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

function ImageUploadModal({ isOpen, onClose, onImageSelected, aspect = 1, title = "Upload Image" }) {
  const { addToast } = useToast();
  const [selectedFile, setSelectedFile] = useState(null);
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Revoke blob URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setImgSrc('');
      setCrop(undefined);
      setCompletedCrop(undefined);
      setScale(1);
      setRotate(0);
    }
  }, [isOpen]);

  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > MAX_FILE_SIZE) {
        addToast('File is too large. Please select an image under 5MB.', 'warning');
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(file);
    }
  }

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    const newCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        width,
        height,
      ),
      width,
      height,
    );
    setCrop(newCrop);
    imgRef.current = e.currentTarget;
  }

  async function handleSave() {
    if (completedCrop && imgRef.current) {
        // Generate cropped image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
        const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

        const pixelRatio = window.devicePixelRatio;
        canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
        canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);

        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';

        const cropX = completedCrop.x * scaleX;
        const cropY = completedCrop.y * scaleY;

        const rotateRads = rotate * Math.PI / 180;
        const centerX = imgRef.current.naturalWidth / 2;
        const centerY = imgRef.current.naturalHeight / 2;

        ctx.save();
        ctx.translate(-cropX, -cropY);
        ctx.translate(centerX, centerY);
        ctx.rotate(rotateRads);
        ctx.scale(scale, scale);
        ctx.translate(-centerX, -centerY);
        ctx.drawImage(
            imgRef.current,
            0,
            0,
            imgRef.current.naturalWidth,
            imgRef.current.naturalHeight,
            0,
            0,
            imgRef.current.naturalWidth,
            imgRef.current.naturalHeight,
        );
        ctx.restore();

        canvas.toBlob((blob) => {
            if (blob) {
                // Revoke previous blob URL before creating a new one
                if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
                // Create a new File object
                const file = new File([blob], selectedFile.name, { type: selectedFile.type });
                const url = URL.createObjectURL(blob);
                blobUrlRef.current = url;
                onImageSelected(file, url);
                onClose();
            }
        }, selectedFile.type);
    } else if (selectedFile) {
        // No crop, just return original
         onImageSelected(selectedFile, imgSrc);
         onClose();
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
            className="fixed inset-0 bg-warm-dark/80 backdrop-blur-sm transition-opacity" 
            aria-hidden="true" 
            onClick={onClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal Panel */}
        <div className="inline-block align-bottom bg-warm-dark rounded-[2rem] text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-white/10">
          
          {/* Header */}
          <div className="bg-black/20 px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white flex items-center gap-2" id="modal-title">
              <FiImage className="text-slate-blue" />
              {title}
            </h3>
            <button 
                onClick={onClose} 
                className="text-white/40 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-2 rounded-full"
            >
              <span className="sr-only">Close</span>
              <FiX className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 py-6">
            {!imgSrc ? (
                // Upload State
                <div 
                    className="border-2 border-dashed border-white/10 rounded-2xl p-12 text-center hover:border-slate-blue/50 hover:bg-slate-blue/5 transition-all cursor-pointer group"
                    onClick={() => fileInputRef.current?.click()}
                >
                    <div className="bg-slate-blue/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-glow-sm">
                        <FiUpload className="h-10 w-10 text-slate-blue" />
                    </div>
                    <p className="text-xl font-bold text-white mb-2">Click to upload image</p>
                    <p className="text-sm text-cream/50">SVG, PNG, JPG or GIF (max. 5MB)</p>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={onSelectFile} 
                        accept="image/*" 
                        className="hidden" 
                    />
                </div>
            ) : (
                // Edit/Crop State
                <div className="space-y-6">
                    <div className="bg-black/40 rounded-xl overflow-hidden shadow-inner border border-white/5 relative min-h-[400px] flex items-center justify-center">
                        <ReactCrop
                            crop={crop}
                            onChange={(_, percentCrop) => setCrop(percentCrop)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={aspect}
                            className="max-h-[60vh]"
                        >
                            <img
                                ref={imgRef}
                                alt="Crop me"
                                src={imgSrc}
                                style={{ transform: `scale(${scale}) rotate(${rotate}deg)`, maxHeight: '60vh' }}
                                onLoad={onImageLoad}
                                className="block max-w-full"
                            />
                        </ReactCrop>
                    </div>

                    {/* Controls */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                             <label className="text-xs font-bold uppercase tracking-wider text-slate-blue flex items-center gap-2">
                                <FiZoomIn /> Zoom
                             </label>
                             <input
                                type="range"
                                min="0.5"
                                max="3"
                                step="0.1"
                                value={scale}
                                disabled={!imgSrc}
                                onChange={(e) => setScale(Number(e.target.value))}
                                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-slate-blue"
                             />
                        </div>
                         <div className="space-y-2">
                             <label className="text-xs font-bold uppercase tracking-wider text-slate-blue flex items-center gap-2">
                                <FiRefreshCw /> Rotate
                             </label>
                             <input
                                type="range"
                                min="0"
                                max="360"
                                value={rotate}
                                disabled={!imgSrc}
                                onChange={(e) => setRotate(Math.min(360, Math.max(0, Number(e.target.value))))}
                                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-slate-blue"
                             />
                        </div>
                    </div>
                </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-black/20 px-6 py-4 border-t border-white/5 flex flex-col sm:flex-row sm:justify-between items-center gap-4">
            {imgSrc && (
                <button
                    type="button"
                    className="text-sm text-cream/60 hover:text-white underline decoration-dotted underline-offset-4 hover:decoration-solid transition-colors"
                    onClick={() => {
                        setImgSrc('');
                        setSelectedFile(null);
                    }}
                >
                    Remove & Upload New
                </button>
            )}
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto ml-auto">
                <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-3 rounded-xl border border-white/10 text-cream font-bold hover:bg-white/5 active:bg-white/10 transition-colors"
                    onClick={onClose}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-3 rounded-xl bg-slate-blue text-warm-dark font-bold shadow-glow hover:bg-slate-blue/90 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSave}
                    disabled={!imgSrc}
                >
                    <FiCheck />
                    {completedCrop ? 'Crop & Save' : 'Save Original'}
                </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ImageUploadModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onImageSelected: PropTypes.func.isRequired,
    aspect: PropTypes.number,
    title: PropTypes.string,
};

export default ImageUploadModal;